'use client';

import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { SelfCheckoutSubscriptionStatus } from '@/prisma/types';
import { Loader } from '@/ui-lib/loader';

import { SubscriptionsListResponse, fetchSubscriptions } from './api';

export function ActiveSubscriptionGuard({
    children,
    cartPageType,
}: {
    children: React.ReactNode;
    cartPageType: 'platform' | 'market-report';
}) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { status } = useSession();
    const [shouldRedirect, setShouldRedirect] = useState(false);

    // Get subscriptionId from query params
    const subscriptionIdParam = searchParams.get('subscriptionId');
    const hasSubscriptionIdInUrl = subscriptionIdParam !== null;

    // Only fetch subscriptions if user is authenticated and no subscriptionId in URL
    const shouldFetchSubscriptions = status === 'authenticated' && !hasSubscriptionIdInUrl;

    const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = useQuery<SubscriptionsListResponse, Error>({
        queryKey: ['user-subscriptions'],
        queryFn: fetchSubscriptions,
        enabled: shouldFetchSubscriptions,
    });

    useEffect(() => {
        // If we fetched subscriptions and found an active one, redirect
        if (shouldFetchSubscriptions && subscriptionsData && subscriptionsData.subscriptions.length > 0) {
            const subscriptionsOfType = subscriptionsData.subscriptions.filter((sub) => {
                if (cartPageType === 'platform') {
                    return sub.items.some((item) => item.type === 'platform');
                } else if (cartPageType === 'market-report') {
                    return sub.items.some((item) => item.type === 'group');
                }
                return false;
            });

            // Find an active subscription (not pending cancellation)
            const activeSubscription = subscriptionsOfType.find(
                (sub) => sub.status === SelfCheckoutSubscriptionStatus.ACTIVE && !sub.cancelAtPeriodEnd
            );

            // If no active subscription, find any subscription that's active (even if pending cancellation)
            const anySubscription =
                activeSubscription ||
                subscriptionsOfType.find((sub) => sub.status === SelfCheckoutSubscriptionStatus.ACTIVE);

            if (anySubscription) {
                setShouldRedirect(true);

                if (cartPageType === 'platform') {
                    router.replace(`/billing/manage-subscription`);
                } else if (cartPageType === 'market-report') {
                    router.replace(`/market-reports/cart?subscriptionId=${anySubscription.id}`);
                }
            }
        }
    }, [shouldFetchSubscriptions, subscriptionsData, router, cartPageType]);

    // Show loading while checking for existing subscription
    if (shouldFetchSubscriptions && (isLoadingSubscriptions || shouldRedirect)) {
        return (
            <div className="min-h-screen w-full bg-neutral-950 flex items-center justify-center">
                <Loader mode="light" size="md" />
            </div>
        );
    }

    return <>{children}</>;
}
